import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { textType } from 'resources/AppTexts';
import { useRef, useState } from 'react';
import { Grid, IconButton, Typography } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { AppColors } from 'resources/AppColors';
import { useDispatch, useSelector } from 'react-redux';
import DialogMonitoring from 'components/DialogMonitoring';
import {
  CommonTexts,
  LabelsLogOut,
  PatientsTableModals,
  patientsTexts,
} from 'locale/es';
import TextMonitoring from 'components/TextMonitoring';
import CustomDialog from 'components/Dialogs/MasterDialog';
import PatientToDisplay from './PatientToDisplay';
import {
  createPatientAction,
  deletePatientAction,
  importPatientsAction,
  updatePatientAction,
} from 'redux/reducers/allPatientsReducer';
import NoDataTableBody from 'components/TableMonitoring/Body/NoDataTableBody';
import Loader from 'components/Loader/Loader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileImport } from '@fortawesome/free-solid-svg-icons';
import { encodeImageFileAsURL } from 'utils/HelperFunctions';
import { PatientForm } from './PatientForm/PatientForm';
import { useFilterContext } from 'context/FilterContext';
import { useFilter } from 'hooks/useFilter';
import FilterTable from 'components/TableMonitoring/Toolbar/Filter';

const tableHeaders = [
  { name: CommonTexts.FULL_NAME, value: 'fullName' },
  { name: CommonTexts.EMAIL, value: 'email' },
  { name: CommonTexts.BIRTHDAY, value: 'birthDay' },
  { name: CommonTexts.SEX, value: 'sex' },
  { name: patientsTexts.CREATION_DATE, value: 'createdDate' },
  { name: patientsTexts.FIRST_LOGIN, value: 'firstLogin' },
  { name: patientsTexts.LAST_ACCESS, value: 'lastAccess' },
  { name: patientsTexts.SELFREGISTRATION_TITLE, value: 'checkbox' },

];

const PatientsTable = ({ centerId }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const fileInputRef = useRef(null);

  const { selectedFilter, searchTerm } = useFilterContext();

  const dispatch = useDispatch();

  const { data, loading, error } = useSelector(
    ({ allPatients }) => allPatients
  );

  const adaptedTableHeaders = tableHeaders.map((header) => header.name);
  const { filteredData } = useFilter(data, adaptedTableHeaders);

  const handleShowDeleteModal = (patientToDelete) => {
    if (patientToDelete) {
      setSelectedPatient(patientToDelete);
    }
    setShowDeleteModal(!showDeleteModal);
  };

  const handleOnConfirmDeletePatient = () => {
    // ? Will probably need to send center id as well
    dispatch(deletePatientAction(selectedPatient.id));
    setShowDeleteModal(false);
    setSelectedPatient(null);
  };

  const handleShowEditModal = (patient) => {
    if (patient.id) {
      setSelectedPatient(patient);
    } else {
      setSelectedPatient(null);
    }
    setShowEditModal(!showEditModal);
  };

  const handleShowAddModal = () => {
    setShowAddModal(!showAddModal);
  };

  const handleFile = (e) => {
    const selectedFile = e.target.files[0];

    if (selectedFile) {
      encodeImageFileAsURL(selectedFile, (result) => {
        const parsedFileString = result?.replace(/^data:text\/csv;base64,/, '');
        submitImportPatients(parsedFileString);
        fileInputRef.current.value = null;
      });
    }
  };

  const handleImportPatients = () => {
    fileInputRef.current.click();
  };

  const submitImportPatients = async (file) => {
    const patients = {
      centerId,
      patientsData: file,
    };
    dispatch(importPatientsAction(patients));
  };

  // * await to handle closing the modal properly
  async function addParticipant(patientObject) {
    await dispatch(createPatientAction(patientObject));
  }

  // * await to handle closing the modal properly
  async function editPatient(patientObject) {
    await dispatch(
      updatePatientAction({ ...patientObject, patientId: selectedPatient.id })
    );
  }

  if (loading) {
    return <Loader message={patientsTexts.LOADING} pt={32} />;
  }

  return (
    <>
      <FilterTable data={data} tableHeaders={tableHeaders} />
      <TableContainer component={Paper}>
        <input
          id='fileInput'
          type='file'
          accept='.csv'
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFile}
        />
        <IconButton onClick={handleImportPatients} size='large'>
          <FontAwesomeIcon
            icon={faFileImport}
            size='2xs'
            fixedWidth
            style={{ color: AppColors.PRIMARY }}
          />
          <Typography variant='menu_list_text' sx={{ ml: 0.5, mt: 0.5 }}>
            {CommonTexts.IMPORT}
          </Typography>
        </IconButton>
        <Table aria-label='simple table'>
          <TableHead>
            <TableRow>
              {adaptedTableHeaders.map((header) => (
                <TableCell align='left' key={header}>
                  <Typography variant='subtitle'>{header}</Typography>
                </TableCell>
              ))}
              <TableCell align='center'>
                <IconButton onClick={handleShowAddModal} size='large'>
                  <AddCircleIcon sx={{ fill: AppColors.PRIMARY }} />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length > 0 && (
              <>
                {(selectedFilter && !searchTerm ? data : filteredData).map(
                  (patient) => (
                    <PatientToDisplay
                      key={patient.id}
                      patient={patient}
                      handleShowEditModal={handleShowEditModal}
                      handleShowDeleteModal={handleShowDeleteModal}
                    />
                  )
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {data?.length === 0 && (
        <NoDataTableBody
          entity={centerId}
          noDataMessage={patientsTexts.NO_DATA}
        />
      )}
      {data?.length > 0 && filteredData?.length === 0 && searchTerm && (
        <NoDataTableBody
          entity={centerId}
          noDataMessage={patientsTexts.NO_DATA_FILTERS}
        />
      )}
      <CustomDialog
        title={PatientsTableModals.CREATE_PATIENT}
        show={showAddModal}
        handleShow={handleShowAddModal}
      >
        <PatientForm
          allPatients={data}
          selectedCenterId={centerId}
          setShowModal={setShowAddModal}
          onSubmit={addParticipant}
        />
      </CustomDialog>
      <CustomDialog
        title={PatientsTableModals.EDIT_PATIENT}
        show={showEditModal}
        handleShow={handleShowEditModal}
      >
        <PatientForm
          allPatients={data}
          selectedCenterId={centerId}
          setShowModal={setShowEditModal}
          onSubmit={editPatient}
          selectedPatient={selectedPatient}
          isEdit
        />
      </CustomDialog>
      {showDeleteModal && (
        <DialogMonitoring
          textCancelButton={LabelsLogOut.CANCEL}
          title={PatientsTableModals.DELETE_PATIENT.TITLE}
          textConfirmButton={CommonTexts.DELETE}
          buttonColor={AppColors.RED}
          show={showDeleteModal}
          handleShow={handleShowDeleteModal}
          handleConfirm={handleOnConfirmDeletePatient}
        >
          <Grid>
            <TextMonitoring
              color={AppColors.PRIMARY}
              type={textType.BODY}
              text={PatientsTableModals.DELETE_PATIENT.TEXT}
            />
          </Grid>
        </DialogMonitoring>
      )}
    </>
  );
};

export default PatientsTable;
